<template>
    <div class="min-h-screen blog-container">
        <!-- 重新设计的导航栏 -->
        <nav class="navbar" :class="{ 'navbar-hidden': !showNavbar }">
            <div class="navbar-container">
                <div class="flex items-center justify-between">
                    <!-- 左侧 Logo -->
                    <div class="flex items-center cursor-pointer select-none group" @click="$router.push('/')">
                        <div class="logo-container">
                            <img src="/images/mahiru-logo.svg" alt="Mahiru Logo" class="logo-svg" />
                        </div>
                    </div>

                    <!-- 中间导航菜单 -->
                    <div class="hidden md:flex items-center gap-2">
                        <button @click="activeView = 'articles'" class="nav-tab"
                            :class="{ active: activeView === 'articles' }">
                            <BookOpenIcon :size="16" />
                            <span>文章</span>
                        </button>
                        <a href="/about" class="nav-tab">
                            <InfoIcon :size="16" />
                            <span>关于</span>
                        </a>
                        <button v-if="user" @click="activeView = 'profile'" class="nav-tab"
                            :class="{ active: activeView === 'profile' }">
                            <UserIcon :size="16" />
                            <span>个人中心</span>
                        </button>
                    </div>

                    <!-- 右侧搜索和用户 -->
                    <div class="flex items-center gap-3">
                        <!-- 搜索按钮 -->
                        <button @click="showSearchModal = true" class="search-button">
                            <SearchIcon :size="18" />
                        </button>

                        <!-- 用户信息 -->
                        <template v-if="user">
                            <div class="user-info-container cursor-pointer" @click="activeView = 'profile'">
                                <div class="user-avatar-wrapper">
                                    <a-avatar :size="32" :src="profile?.avatar_url" class="user-avatar">
                                        {{ (profile?.display_name || profile?.username || user.email?.split('@')[0]
                                            || 'U')[0]?.toUpperCase() }}
                                    </a-avatar>
                                    <div class="user-status-dot"></div>
                                </div>
                                <div class="hidden sm:block user-text">
                                    <p class="user-name">{{ profile?.display_name || profile?.username ||
                                        user.email?.split('@')[0] || '用户' }}</p>
                                    <p class="user-status">在线</p>
                                </div>
                            </div>
                        </template>
                        <template v-else>
                            <router-link to="/auth" class="login-button">
                                登录
                            </router-link>
                        </template>
                    </div>
                </div>
            </div>
        </nav>

        <!-- 搜索模态框 -->
        <div v-if="showSearchModal" @click="showSearchModal = false" class="search-modal-overlay">
            <div class="search-modal-backdrop"></div>
            <div @click.stop class="search-modal-container">
                <div class="search-modal">
                    <div class="search-input-container">
                        <div class="search-input-wrapper">
                            <SearchIcon :size="20" class="search-input-icon" />
                            <input ref="searchInput" v-model="searchQuery" @keyup.escape="showSearchModal = false"
                                @input="handleSearch" type="text" placeholder="搜索文章、标签或分类..." class="search-input" />
                            <button @click="showSearchModal = false" class="search-close">
                                <XIcon :size="20" />
                            </button>
                        </div>
                    </div>
                    <div class="search-results">
                        <div v-if="searchResults.length === 0 && searchQuery.trim()" class="search-empty">
                            <SearchIcon :size="48" class="search-empty-icon" />
                            <p>未找到相关内容</p>
                        </div>
                        <div v-else-if="searchResults.length > 0" class="search-results-list">
                            <div v-for="result in searchResults" :key="result.id" @click="openSearchResult(result)"
                                class="search-result-item">
                                <h4 class="search-result-title">{{ result.title }}</h4>
                                <p class="search-result-excerpt">{{ result.excerpt }}</p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <!-- 主内容区 -->
        <main class="main-content">
            <!-- 文章浏览视图 -->
            <div v-if="activeView === 'articles'" class="content-grid">
                <!-- 文章列表区 -->
                <section class="articles-section">

                    <div class="articles-grid">
                        <div v-for="(article, index) in displayArticles" :key="article.id" @click="openArticle(article)"
                            class="article-card-wrapper" :class="{ 'cover-right': index % 2 === 1 }">
                            <article class="article-card">
                                <!-- 封面图片区域（只在有图片时显示） -->
                                <div v-if="article.cover_image_url" class="article-cover">
                                    <img :src="article.cover_image_url" :alt="article.title" class="cover-image" />
                                </div>

                                <!-- 内容区域 -->
                                <div class="article-content">
                                    <!-- 标题与分类（分类在左，标题在右） -->
                                    <div class="title-with-category">
                                        <span v-if="article.category_id" class="article-category-tag">
                                            {{ categoryNameById[article.category_id] || '未分类' }}
                                        </span>
                                        <h2 class="article-title">{{ article.title }}</h2>
                                    </div>

                                    <!-- 摘要 -->
                                    <p class="article-excerpt">
                                        {{ article.summary || article.excerpt || '暂无摘要...' }}
                                    </p>

                                    <!-- 标签 -->
                                    <div v-if="article.tags && article.tags.length > 0" class="article-tags">
                                        <span v-for="tag in article.tags.slice(0, 3)" :key="tag.id"
                                            @click.stop="filterByTag(tag)" class="article-tag"
                                            :style="{ '--tag-color': tag.color || '#6366f1' }">
                                            {{ tag.name }}
                                        </span>
                                        <span v-if="article.tags.length > 3" class="more-tags">
                                            +{{ article.tags.length - 3 }}
                                        </span>
                                    </div>

                                    <!-- 文章信息 -->
                                    <div class="article-meta">
                                        <div class="meta-item">
                                            <FileTextIcon :size="14" />
                                            <span>{{ article.word_count || 0 }} 字</span>
                                        </div>
                                        <div class="meta-item">
                                            <ClockIcon :size="14" />
                                            <span>{{ calculateReadingTime(article.word_count) }}</span>
                                        </div>
                                        <div class="meta-item">
                                            <CalendarIcon :size="14" />
                                            <span>{{ formatRelativeTime(article.updated_at) }}</span>
                                        </div>
                                    </div>
                                </div>
                            </article>
                        </div>
                    </div>

                    <!-- 加载更多 -->
                    <div class="load-more-container">
                        <button :disabled="loadingArticles || !nextArticleCursor" @click="loadMoreArticles()"
                            class="load-more-button">
                            <span v-if="loadingArticles" class="loading-spinner"></span>
                            {{ loadingArticles ? '加载中...' : (nextArticleCursor ? '加载更多文章' : '已加载全部') }}
                        </button>
                    </div>
                </section>

                <!-- 自然轻盈的侧边栏 -->
                <aside class="sidebar">
                    <!-- 文章分类 - 简约卡片流 -->
                    <div class="sidebar-widget">
                        <div class="widget-header">
                            <div class="widget-title-group">
                                <div class="widget-icon category-icon">
                                    <FolderIcon :size="16" />
                                </div>
                                <h3 class="widget-title">文章分类</h3>
                                <span class="widget-count">{{ categories.length }}</span>
                            </div>

                        </div>
                        <div class="category-flow">
                            <!-- 全部分类选项 -->
                            <div @click="clearCategory()" class="category-chip" :class="{ 'active': !activeCategory }">
                                <span class="chip-name">全部</span>
                                <span class="chip-count">{{ articles.length }}</span>
                                <div class="chip-ripple"></div>
                            </div>

                            <!-- 具体分类 -->
                            <div v-for="category in categories" :key="category.id" @click="filterByCategory(category)"
                                class="category-chip" :class="{ 'active': activeCategory === category.id }">
                                <span class="chip-name">{{ category.name }}</span>
                                <span class="chip-count">{{ getCategoryCount(category.id) }}</span>
                                <div class="chip-ripple"></div>
                            </div>
                        </div>
                    </div>

                    <!-- 热门标签 - 活力标签云 -->
                    <div class="sidebar-widget">
                        <div class="widget-header">
                            <div class="widget-title-group">
                                <div class="widget-icon tag-icon">
                                    <TagIcon :size="16" />
                                </div>
                                <h3 class="widget-title">热门标签</h3>
                                <span class="widget-count">{{ tags.length }}</span>
                            </div>
                            <button v-if="activeTags.length > 0" @click="clearTags" class="clear-filter-btn">
                                <XIcon :size="12" />
                            </button>
                        </div>
                        <div class="tag-constellation">
                            <div v-for="(tag, index) in tags" :key="tag.id" @click="filterByTag(tag)" class="tag-star"
                                :class="{
                                    'active': activeTags.includes(tag.id),
                                    'hot': tag.usage_count >= 5,
                                    'warm': tag.usage_count >= 3 && tag.usage_count < 5,
                                    'cool': tag.usage_count < 3
                                }" :style="{
                                    '--tag-color': tag.color || getRandomTagColor(),
                                    '--delay': index * 0.1 + 's'
                                }">
                                <div class="star-content">
                                    <span class="star-symbol">#</span>
                                    <span class="star-name">{{ tag.name }}</span>
                                    <span class="star-count">{{ tag.usage_count || 0 }}</span>
                                </div>
                                <div class="star-glow"></div>
                            </div>
                        </div>
                        <div class="load-more-section" v-if="nextTagCursor || loadingTags">
                            <button :disabled="loadingTags || !nextTagCursor" @click="loadMoreTags()"
                                class="load-more-elegant">
                                <span v-if="loadingTags" class="elegant-loading">
                                    <span class="loading-pulse"></span>
                                    加载中
                                </span>
                                <span v-else class="load-more-text">
                                    <span class="more-icon">+</span>
                                    发现更多
                                </span>
                            </button>
                        </div>
                    </div>

                    <!-- 统计信息卡片 -->
                    <div class="sidebar-widget stats-widget">
                        <div class="stats-content">
                            <div class="stat-item">
                                <div class="stat-icon articles-stat">
                                    <BookOpenIcon :size="14" />
                                </div>
                                <div class="stat-text">
                                    <span class="stat-number">{{ articles.length }}+</span>
                                    <span class="stat-label">文章</span>
                                </div>
                            </div>
                            <div class="stat-divider"></div>
                            <div class="stat-item">
                                <div class="stat-icon tags-stat">
                                    <TagIcon :size="14" />
                                </div>
                                <div class="stat-text">
                                    <span class="stat-number">{{ tags.length }}+</span>
                                    <span class="stat-label">标签</span>
                                </div>
                            </div>
                            <div class="stat-divider"></div>
                            <div class="stat-item">
                                <div class="stat-icon categories-stat">
                                    <FolderIcon :size="14" />
                                </div>
                                <div class="stat-text">
                                    <span class="stat-number">{{ categories.length }}</span>
                                    <span class="stat-label">分类</span>
                                </div>
                            </div>
                        </div>
                    </div>
                </aside>
            </div>

            <!-- 个人中心视图 -->
            <div v-else-if="activeView === 'profile'" class="profile-view">
                <Profile />
            </div>
        </main>

        <!-- 模态框 -->
    </div>
</template>

<script>
import supabaseArticleService from '../services/supabaseArticleService.js'
import supabaseTagService from '../services/supabaseTagService.js'
import supabaseCategoryService from '../services/supabaseCategoryService.js'
import articleDataCacheService from '../services/articleDataCacheService.js'
import supabaseAuthService from '../services/supabaseAuthService.js'
import supabase from '../services/supabaseClient.js'
import supabaseProfileService from '../services/supabaseProfileService.js'
import Profile from './Profile.vue'

import {
    SearchIcon,
    XIcon,
    UserIcon,
    LogOutIcon,
    BookOpenIcon,
    FolderIcon,
    TagIcon,
    FileTextIcon,
    ClockIcon,
    CalendarIcon,
    InfoIcon,
    ChevronRightIcon
} from 'lucide-vue-next'

// Ant Design 组件
import { Avatar, Dropdown, Menu } from 'ant-design-vue'

export default {
    name: 'ArticleBrowse',
    components: {
        'a-avatar': Avatar,
        'a-dropdown': Dropdown,
        'a-menu': Menu,
        'a-menu-item': Menu.Item,
        Profile,

        SearchIcon,
        XIcon,
        UserIcon,
        LogOutIcon,
        BookOpenIcon,
        FolderIcon,
        TagIcon,
        FileTextIcon,
        ClockIcon,
        CalendarIcon,
        InfoIcon,
        ChevronRightIcon
    },
    data() {
        return {
            // 文章数据
            articles: [],
            nextArticleCursor: null,
            loadingArticles: false,

            // 标签数据
            tags: [],
            nextTagCursor: null,
            loadingTags: false,

            // 分类数据
            categories: [],
            categoryNameById: {},

            // 过滤状态
            activeCategory: null,
            activeTags: [], // 改为数组支持多选
            searchQuery: '',
            searchResults: [],

            // 用户状态
            user: null,
            profile: null,

            // 视图状态
            activeView: 'articles', // 'articles' 或 'profile'

            // UI 状态
            showSearchModal: false,
            showNavbar: true,
            lastScrollY: 0,
        }
    },
    computed: {
        displayArticles() {
            return this.articles
        }
    },
    async mounted() {
        // 智能清理过期缓存
        articleDataCacheService.smartCleanup()

        // 键盘快捷键
        document.addEventListener('keydown', this.handleKeydown)
        // 滚动监听
        window.addEventListener('scroll', this.handleScroll)

        await Promise.all([
            this.loadMoreArticles(true),
            this.loadMoreTags(true),
            this.loadCategories(),
            this.fetchUser(),
        ])
    },
    beforeUnmount() {
        document.removeEventListener('keydown', this.handleKeydown)
        window.removeEventListener('scroll', this.handleScroll)
    },
    methods: {
        handleScroll() {
            const currentScrollY = window.scrollY

            // 如果向下滚动且滚动距离超过100px，隐藏导航栏
            if (currentScrollY > this.lastScrollY && currentScrollY > 100) {
                this.showNavbar = false
            }
            // 如果向上滚动，显示导航栏
            else if (currentScrollY < this.lastScrollY) {
                this.showNavbar = true
            }

            this.lastScrollY = currentScrollY
        },

        handleKeydown(e) {
            // Cmd/Ctrl + K 打开搜索
            if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
                e.preventDefault()
                this.showSearchModal = true
                this.$nextTick(() => {
                    this.$refs.searchInput?.focus()
                })
            }
            // Esc 关闭搜索
            if (e.key === 'Escape' && this.showSearchModal) {
                this.showSearchModal = false
            }
        },

        async handleSearch() {
            if (!this.searchQuery.trim()) {
                this.searchResults = []
                return
            }

            // 简单的本地搜索
            const query = this.searchQuery.toLowerCase()
            this.searchResults = this.articles.filter(article =>
                article.title.toLowerCase().includes(query) ||
                (article.summary && article.summary.toLowerCase().includes(query)) ||
                (article.excerpt && article.excerpt.toLowerCase().includes(query))
            ).slice(0, 5)
        },

        openSearchResult(result) {
            this.showSearchModal = false
            this.openArticle(result)
        },

        async fetchUser() {
            try {
                this.user = await supabaseAuthService.getCurrentUser()
                if (this.user) {
                    this.profile = await supabaseProfileService.getUserProfile(this.user.id)
                    console.log('用户资料加载成功:', this.profile) // 调试日志
                } else {
                    this.profile = null
                }
            } catch (e) {
                console.warn('获取用户信息失败', e)
                this.profile = null
            }
        },

        async logout() {
            try {
                await supabaseAuthService.signOut()
                this.user = null
                this.profile = null
            } catch (e) {
                console.error('退出登录失败', e)
            }
        },

        async loadCategories() {
            try {
                // 使用缓存服务加载分类
                this.categories = await articleDataCacheService.getCategories()
                this.categoryNameById = Object.fromEntries(this.categories.map(c => [c.id, c.name]))
            } catch (e) {
                console.error('加载分类失败', e)
                // 降级到直接调用服务
                try {
                    this.categories = await supabaseCategoryService.list()
                    this.categoryNameById = Object.fromEntries(this.categories.map(c => [c.id, c.name]))
                } catch (fallbackError) {
                    console.error('分类加载完全失败', fallbackError)
                }
            }
        },

        async attachTags(items) {
            if (!items || items.length === 0) return items
            try {
                const articleIds = items.map(i => i.id)
                const { data: atRows } = await supabase
                    .from('article_tags')
                    .select('article_id, tag_id')
                    .in('article_id', articleIds)

                const tagIds = Array.from(new Set((atRows || []).map(r => r.tag_id)))
                let tagMap = {}
                if (tagIds.length > 0) {
                    const { data: tagRows } = await supabase
                        .from('tags')
                        .select('id, name, color')
                        .in('id', tagIds)
                    tagMap = Object.fromEntries((tagRows || []).map(t => [t.id, t]))
                }

                const articleIdToTags = {}
                    ; (atRows || []).forEach(r => {
                        const list = articleIdToTags[r.article_id] || (articleIdToTags[r.article_id] = [])
                        const tag = tagMap[r.tag_id]
                        if (tag) list.push(tag)
                    })

                return items.map(i => ({ ...i, tags: articleIdToTags[i.id] || [] }))
            } catch (e) {
                console.warn('加载文章标签失败：', e)
                return items
            }
        },

        async loadMoreArticles(initial = false) {
            if (this.loadingArticles) return
            this.loadingArticles = true
            try {
                const options = {
                    limit: 10,
                    cursor: initial ? null : this.nextArticleCursor,
                    categoryId: this.activeCategory || undefined,
                    tagIds: this.activeTags.length > 0 ? this.activeTags : undefined,
                }

                // 使用缓存服务加载文章分页数据
                const { items, nextCursor } = await articleDataCacheService.getArticlesPaginated(options)
                const enriched = await this.attachTags(items)
                this.articles = initial ? enriched : [...this.articles, ...enriched]
                this.nextArticleCursor = nextCursor
            } catch (e) {
                console.error('加载文章失败', e)
                // 降级到直接调用服务
                try {
                    const { items, nextCursor } = await supabaseArticleService.listPublishedByCursor({
                        limit: 10,
                        cursor: initial ? null : this.nextArticleCursor,
                        categoryId: this.activeCategory || undefined,
                        tagIds: this.activeTags.length > 0 ? this.activeTags : undefined,
                    })
                    const enriched = await this.attachTags(items)
                    this.articles = initial ? enriched : [...this.articles, ...enriched]
                    this.nextArticleCursor = nextCursor
                } catch (fallbackError) {
                    console.error('文章加载完全失败', fallbackError)
                }
            } finally {
                this.loadingArticles = false
            }
        },

        async loadMoreTags(initial = false) {
            if (this.loadingTags) return
            this.loadingTags = true
            try {
                const options = {
                    limit: 20,
                    cursor: initial ? null : this.nextTagCursor
                }

                // 使用缓存服务加载标签分页数据
                const { items, nextCursor } = await articleDataCacheService.getTagsPaginated(options)
                this.tags = initial ? items : [...this.tags, ...items]
                this.nextTagCursor = nextCursor
            } catch (e) {
                console.error('加载标签失败', e)
                // 降级到直接调用服务
                try {
                    const { items, nextCursor } = await supabaseTagService.listByUsageCursor({
                        limit: 20,
                        cursor: initial ? null : this.nextTagCursor
                    })
                    this.tags = initial ? items : [...this.tags, ...items]
                    this.nextTagCursor = nextCursor
                } catch (fallbackError) {
                    console.error('标签加载完全失败', fallbackError)
                }
            } finally {
                this.loadingTags = false
            }
        },

        filterByTag(tag) {
            // 多选标签逻辑
            const tagId = tag?.id
            if (!tagId) return

            const index = this.activeTags.indexOf(tagId)
            if (index > -1) {
                // 如果已选中，则取消选中
                this.activeTags.splice(index, 1)
            } else {
                // 如果未选中，则添加
                this.activeTags.push(tagId)
            }

            // 重新加载文章
            this.articles = []
            this.nextArticleCursor = null
            this.loadMoreArticles(true)
        },

        clearTags() {
            this.activeTags = []
            this.articles = []
            this.nextArticleCursor = null
            this.loadMoreArticles(true)
        },

        filterByCategory(category) {
            this.activeCategory = category?.id || null
            this.activeTag = null
            this.articles = []
            this.nextArticleCursor = null
            this.loadMoreArticles(true)
        },

        clearCategory() {
            this.activeCategory = null
            this.articles = []
            this.nextArticleCursor = null
            this.loadMoreArticles(true)
        },

        openArticle(article) {
            this.$router.push({ name: 'ArticleDetail', params: { id: article.id.toString() } })
        },

        onAuthSuccess() {
            this.fetchUser()
        },

        calculateReadingTime(wordCount) {
            if (!wordCount) return '1 分钟'
            const minutes = Math.ceil(wordCount / 200) // 假设每分钟阅读200字
            return `${minutes} 分钟`
        },

        formatRelativeTime(dateString) {
            const date = new Date(dateString)
            const now = new Date()
            const diffInSeconds = Math.floor((now - date) / 1000)

            if (diffInSeconds < 60) return '刚刚'
            if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)} 分钟前`
            if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)} 小时前`
            if (diffInSeconds < 604800) return `${Math.floor(diffInSeconds / 86400)} 天前`

            return date.toLocaleDateString('zh-CN', {
                year: 'numeric',
                month: '2-digit',
                day: '2-digit'
            })
        },

        getCategoryCount(categoryId) {
            return this.articles.filter(article => article.category_id === categoryId).length
        },

        getRandomTagColor() {
            const colors = ['#3b82f6', '#8b5cf6', '#10b981', '#f59e0b', '#ef4444', '#06b6d4', '#ec4899']
            return colors[Math.floor(Math.random() * colors.length)]
        },

        getTagIconSize(usageCount) {
            if (usageCount >= 5) return 14
            if (usageCount >= 3) return 12
            return 10
        }
    }
}
</script>

<style scoped>
/* ========== 全局容器 ========== */
.blog-container {
    min-height: 100vh;
    background: url("https://raw.githubusercontent.com/mizorehe70dayo/MahiruBlogWeb/main/public/images/background.jpg");
    background-size: cover;
    background-position: center;
    background-attachment: fixed;
}

/* ========== 简约线条导航栏 ========== */
.navbar {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    z-index: 50;
    background: rgba(255, 255, 255, 0.98);
    border-bottom: 1px solid #e5e7eb;
    backdrop-filter: blur(8px);
    transition: transform 0.2s ease;
    transform: translateY(0);
    height: 64px;
}

.navbar-container {
    max-width: 1400px;
    margin: 0 auto;
    padding: 12px 16px;
}

.navbar-hidden {
    transform: translateY(-100%);
}

.logo-container {
    position: relative;
}

.logo-svg {
    height: 40px;
    width: auto;
    transition: opacity 0.2s ease;
}

.logo-container:hover .logo-svg {
    opacity: 0.8;
}





/* 导航标签样式 */
.nav-tab {
    display: flex;
    align-items: center;
    gap: 6px;
    padding: 8px 16px;
    color: #6b7280;
    text-decoration: none;
    font-weight: 500;
    font-size: 14px;
    border-radius: 6px;
    transition: all 0.2s ease;
    background: transparent;
    border: 1px solid #e5e7eb;
    position: relative;
}

.nav-tab:hover {
    color: #00c851;
    background: rgba(0, 200, 81, 0.05);
    border-color: #00c851;
}

.nav-tab.active {
    color: white;
    background: #00c851;
    border-color: #00c851;
}

/* 搜索按钮 */
.search-button {
    width: 36px;
    height: 36px;
    border-radius: 6px;
    background: white;
    border: 1px solid #e5e7eb;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    transition: all 0.2s ease;
    color: #6b7280;
}

.search-button:hover {
    background: white;
    border-color: #00c851;
    color: #00c851;
}

/* 用户信息 */
.user-info-container {
    display: flex;
    align-items: center;
    gap: 10px;
    cursor: pointer;
    padding: 4px 8px;
    border-radius: 6px;
    transition: background-color 0.2s ease;
}

.user-info-container:hover {
    background: rgba(0, 0, 0, 0.05);
}

.user-avatar-wrapper {
    position: relative;
}

.user-avatar {
    border: 2px solid #e5e7eb;
}

.user-status-dot {
    position: absolute;
    bottom: -1px;
    right: -1px;
    width: 10px;
    height: 10px;
    background: #10b981;
    border: 2px solid white;
    border-radius: 50%;
}

.user-name {
    font-size: 13px;
    font-weight: 600;
    color: #1f2937;
    margin: 0;
}

.user-status {
    font-size: 11px;
    color: #6b7280;
    margin: 0;
}

.login-button {
    padding: 8px 16px;
    background: #00c851;
    color: white;
    border: 1px solid #00c851;
    border-radius: 6px;
    font-weight: 500;
    font-size: 13px;
    cursor: pointer;
    transition: all 0.2s ease;
}

.login-button:hover {
    background: #00a844;
    border-color: #00a844;
}

/* ========== 搜索模态框保持不变 ========== */
.search-modal-overlay {
    position: fixed;
    inset: 0;
    z-index: 50;
    display: flex;
    align-items: flex-start;
    justify-content: center;
    padding-top: 10vh;
    padding: 16px;
}

.search-modal-backdrop {
    position: absolute;
    inset: 0;
    background: rgba(0, 0, 0, 0.6);
    backdrop-filter: blur(4px);
}

.search-modal-container {
    position: relative;
    width: 100%;
    max-width: 600px;
    margin-top: 10vh;
}

.search-modal {
    background: white;
    border-radius: 16px;
    box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
    border: 1px solid rgba(255, 255, 255, 0.2);
    overflow: hidden;
}

.search-input-container {
    padding: 20px;
    border-bottom: 1px solid #f3f4f6;
}

.search-input-wrapper {
    position: relative;
    display: flex;
    align-items: center;
}

.search-input-icon {
    position: absolute;
    left: 16px;
    color: #6b7280;
}

.search-input {
    width: 100%;
    height: 52px;
    padding: 0 48px 0 48px;
    background: transparent;
    border: none;
    font-size: 18px;
    color: #1f2937;
    outline: none;
}

.search-input::placeholder {
    color: #9ca3af;
}

.search-close {
    position: absolute;
    right: 16px;
    padding: 4px;
    background: none;
    border: none;
    color: #6b7280;
    cursor: pointer;
    border-radius: 4px;
    transition: all 0.2s ease;
}

.search-close:hover {
    background: #f3f4f6;
    color: #374151;
}

.search-results {
    max-height: 400px;
    overflow-y: auto;
}

.search-empty {
    padding: 60px 20px;
    text-align: center;
    color: #6b7280;
}

.search-empty-icon {
    margin: 0 auto 16px;
    opacity: 0.5;
}

.search-results-list {
    padding: 8px;
}

.search-result-item {
    padding: 12px;
    border-radius: 8px;
    cursor: pointer;
    transition: background-color 0.2s ease;
}

.search-result-item:hover {
    background: #f9fafb;
}

.search-result-title {
    font-weight: 600;
    color: #1f2937;
    margin: 0 0 4px 0;
}

.search-result-excerpt {
    font-size: 14px;
    color: #6b7280;
    margin: 0;
}

/* ========== 主内容区 ========== */
.main-content {
    max-width: 1400px;
    margin: 0 auto;
    padding: 88px 16px 64px;
    /* 调整顶部padding适应新的导航栏高度 */
    background: transparent;
    backdrop-filter: none;
}

.content-grid {
    display: grid;
    grid-template-columns: 1fr 300px;
    gap: 40px;
    background: transparent;
    backdrop-filter: none;
}

@media (max-width: 1024px) {
    .content-grid {
        grid-template-columns: 1fr;
        gap: 24px;
    }
}

/* ========== 文章区域 ========== */
.articles-section {
    min-width: 0;
    background: transparent !important;
    backdrop-filter: none !important;
}

.articles-section * {
    backdrop-filter: none !important;
}

.section-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 24px;
    padding: 0 4px;
}

.section-title-wrapper {
    display: flex;
    align-items: center;
    gap: 12px;
}

.section-indicator {
    width: 4px;
    height: 24px;
    background: linear-gradient(to bottom, #4f46e5, #7c3aed);
    border-radius: 2px;
}

.section-title {
    font-size: 24px;
    font-weight: 700;
    color: #1f2937;
    margin: 0;
}

.article-count {
    font-size: 14px;
    color: #6b7280;
    background: rgba(255, 255, 255, 0.9);
    padding: 6px 12px;
    border-radius: 20px;
    border: 1px solid rgba(0, 0, 0, 0.05);
}

.articles-grid {
    display: flex;
    flex-direction: column;
    gap: 20px;
    background: transparent;
    backdrop-filter: none;
}

.article-card-wrapper {
    cursor: pointer;
    background: transparent !important;
    backdrop-filter: none !important;
}

.article-card {
    background: rgba(30, 41, 59, 0.98);
    border-radius: 16px;
    overflow: hidden;
    border: 1px solid rgba(71, 85, 105, 0.3);
    transition: all 0.3s ease;
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
    height: 240px;
    /* 增加统一高度 */
    display: flex;
}

/* 默认左图右文 */
.article-card {
    flex-direction: row;
}

/* 右图左文 */
.cover-right .article-card {
    flex-direction: row-reverse;
}

.article-card:hover {
    border-color: #00ff88 !important;
    /* 使用亮绿色高对比度边框，不添加阴影 */
    transform: none !important;
    /* 强制移除全局样式的浮动效果 */
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1) !important;
    /* 保持原有阴影，覆盖全局样式 */
    background: rgba(30, 41, 59, 0.98) !important;
    /* 保持原有背景色 */
}

.article-cover {
    width: 320px;
    /* 进一步增加封面图片宽度 */
    height: 100%;
    overflow: hidden;
    flex-shrink: 0;
}

.cover-image {
    width: 100%;
    height: 100%;
    object-fit: cover;
    transition: transform 0.3s ease;
}

.article-card:hover .cover-image {
    transform: scale(1.05);
    /* 悬浮时图片略微放大 */
}





.article-content {
    flex: 1;
    padding: 20px;
    display: flex;
    flex-direction: column;
    justify-content: space-between;
}

/* 标题与分类 */
.title-with-category {
    display: flex;
    align-items: flex-start;
    gap: 12px;
    margin-bottom: 12px;
}

.article-category-tag {
    display: inline-block;
    background: rgba(59, 130, 246, 0.2);
    color: #93c5fd;
    padding: 4px 8px;
    border-radius: 8px;
    font-size: 11px;
    font-weight: 600;
    white-space: nowrap;
    flex-shrink: 0;
    margin-top: 2px;
    /* 与标题稍微对齐 */
}

.article-title {
    font-size: 18px;
    font-weight: 700;
    color: white;
    margin: 0;
    line-height: 1.3;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
    flex: 1;
    /* 占据剩余空间 */
}

.article-excerpt {
    font-size: 14px;
    color: #cbd5e1;
    line-height: 1.6;
    margin: 0 0 16px 0;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    /* 调整为两行 */
    -webkit-box-orient: vertical;
    overflow: hidden;
}

.article-tags {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    margin-bottom: 16px;
}

.article-tag {
    display: flex;
    align-items: center;
    gap: 6px;
    padding: 4px 10px;
    background: var(--tag-color, #6366f1);
    /* 使用标签颜色作为背景 */
    border: 1px solid var(--tag-color, #6366f1);
    border-radius: 12px;
    font-size: 12px;
    color: white;
    /* 白色文字 */
    cursor: pointer;
    transition: all 0.3s ease;
}

.article-tag:hover {
    background: var(--tag-color, #6366f1);
    border-color: var(--tag-color, #6366f1);
    color: white;
    opacity: 0.9;
    /* 悬浮时略微降低透明度 */
}

/* 不再需要标签点，因为现在使用颜色背景 */
.tag-dot {
    display: none;
}

.more-tags {
    padding: 4px 10px;
    background: rgba(71, 85, 105, 0.4);
    border-radius: 12px;
    font-size: 12px;
    color: #94a3b8;
}

.article-meta {
    display: flex;
    align-items: center;
    gap: 12px;
    font-size: 12px;
    color: #94a3b8;
}

.meta-item {
    display: flex;
    align-items: center;
    gap: 4px;
}

/* 加载更多按钮 */
.load-more-container {
    display: flex;
    justify-content: center;
    margin-top: 32px;
}

.load-more-button {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 12px 24px;
    background: linear-gradient(135deg, #00c851, #00ff88);
    /* 绿色渐变主题 */
    color: white;
    border: none;
    border-radius: 12px;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.3s ease;
    box-shadow: 0 4px 12px rgba(0, 200, 81, 0.3);
}

.load-more-button:hover:not(:disabled) {
    background: linear-gradient(135deg, #00a844, #00e676);
    transform: translateY(-1px);
    box-shadow: 0 6px 20px rgba(0, 200, 81, 0.4);
}

.load-more-button:disabled {
    opacity: 0.6;
    cursor: not-allowed;
}

.loading-spinner {
    width: 16px;
    height: 16px;
    border: 2px solid rgba(255, 255, 255, 0.3);
    border-top: 2px solid white;
    border-radius: 50%;
    animation: spin 1s linear infinite;
}

@keyframes spin {
    to {
        transform: rotate(360deg);
    }
}

/* ========== 自然轻盈的侧边栏 ========== */
.sidebar {
    display: flex;
    flex-direction: column;
    gap: 24px;
}

.sidebar-widget {
    background: rgba(255, 255, 255, 0.85);
    border-radius: 16px;
    padding: 20px;
    border: 1px solid rgba(255, 255, 255, 0.3);
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.06);
    transition: all 0.3s ease;
}

.sidebar-widget:hover {
    background: rgba(255, 255, 255, 0.95);
    box-shadow: 0 8px 30px rgba(0, 0, 0, 0.1);
    transform: translateY(-1px);
}

/* 组件头部样式 */
.widget-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 16px;
}

.widget-title-group {
    display: flex;
    align-items: center;
    gap: 8px;
}

.widget-icon {
    width: 28px;
    height: 28px;
    border-radius: 8px;
    display: flex;
    align-items: center;
    justify-content: center;
    color: white;
}

.category-icon {
    background: linear-gradient(135deg, #00c851, #00a844);
}

.tag-icon {
    background: linear-gradient(135deg, #00ff88, #00e676);
}

.widget-title {
    font-size: 15px;
    font-weight: 600;
    color: #1f2937;
    margin: 0;
}

.widget-count {
    font-size: 11px;
    background: rgba(0, 200, 81, 0.1);
    color: #00c851;
    padding: 2px 6px;
    border-radius: 8px;
    font-weight: 500;
}

.clear-filter-btn {
    width: 24px;
    height: 24px;
    border-radius: 6px;
    background: rgba(239, 68, 68, 0.1);
    border: none;
    color: #ef4444;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    transition: all 0.2s ease;
}

.clear-filter-btn:hover {
    background: rgba(239, 68, 68, 0.2);
    color: #dc2626;
}

/* 分类卡片流样式 */
.category-flow {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
}

.category-chip {
    position: relative;
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 8px 12px;
    background: rgba(0, 200, 81, 0.08);
    border: 1px solid rgba(0, 200, 81, 0.2);
    border-radius: 12px;
    cursor: pointer;
    transition: all 0.3s ease;
    font-size: 13px;
    color: #00c851;
    overflow: hidden;
}

.category-chip:hover {
    background: rgba(0, 200, 81, 0.15);
    border-color: rgba(0, 200, 81, 0.8);
}

.category-chip.active {
    background: #00c851;
    border-color: #00c851;
    color: white;
    box-shadow: 0 4px 12px rgba(0, 200, 81, 0.4);
}

.chip-name {
    font-weight: 500;
}

.chip-count {
    font-size: 11px;
    background: rgba(255, 255, 255, 0.8);
    color: #00c851;
    padding: 2px 6px;
    border-radius: 8px;
    font-weight: 600;
    min-width: 18px;
    text-align: center;
}

.category-chip.active .chip-count {
    background: rgba(255, 255, 255, 0.2);
    color: white;
}

.chip-ripple {
    position: absolute;
    inset: 0;
    background: rgba(255, 255, 255, 0.3);
    border-radius: inherit;
    transform: scale(0);
    opacity: 0;
    pointer-events: none;
}

.category-chip:active .chip-ripple {
    transform: scale(1);
    opacity: 1;
    transition: all 0.3s ease-out;
}

/* 标签星座云样式 */
.tag-constellation {
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
    margin-bottom: 16px;
}

.tag-star {
    position: relative;
    display: inline-flex;
    align-items: center;
    cursor: pointer;
    opacity: 0;
    animation: starAppear 0.6s ease-out forwards;
    animation-delay: var(--delay);
}

@keyframes starAppear {
    from {
        opacity: 0;
        transform: translateY(10px) scale(0.8);
    }

    to {
        opacity: 1;
        transform: translateY(0) scale(1);
    }
}

.star-content {
    position: relative;
    display: flex;
    align-items: center;
    gap: 4px;
    padding: 6px 10px;
    background: rgba(255, 255, 255, 0.9);
    border: 1px solid rgba(0, 0, 0, 0.1);
    border-radius: 14px;
    transition: all 0.3s ease;
    z-index: 2;
}

.tag-star:hover .star-content {
    background: var(--tag-color);
    border-color: var(--tag-color);
    color: white;
}

.tag-star.active .star-content {
    background: var(--tag-color);
    border-color: var(--tag-color);
    color: white;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
}

.star-symbol {
    font-size: 12px;
    font-weight: 600;
    color: var(--tag-color);
    opacity: 0.7;
}

.tag-star:hover .star-symbol,
.tag-star.active .star-symbol {
    color: white;
    opacity: 1;
}

.star-name {
    font-size: 12px;
    font-weight: 500;
    color: #374151;
}

.tag-star:hover .star-name,
.tag-star.active .star-name {
    color: white;
}

.star-count {
    font-size: 10px;
    font-weight: 600;
    background: rgba(0, 0, 0, 0.1);
    color: #6b7280;
    padding: 2px 5px;
    border-radius: 8px;
    min-width: 16px;
    text-align: center;
}

.tag-star:hover .star-count,
.tag-star.active .star-count {
    background: rgba(255, 255, 255, 0.2);
    color: white;
}

.star-glow {
    position: absolute;
    inset: -2px;
    background: var(--tag-color);
    border-radius: 16px;
    opacity: 0;
    blur: 4px;
    transition: opacity 0.3s ease;
    z-index: 1;
}

.tag-star:hover .star-glow {
    opacity: 0.3;
}

.tag-star.active .star-glow {
    opacity: 0.4;
}

/* 热度不同大小 */
.tag-star.hot .star-content {
    padding: 8px 12px;
    font-size: 13px;
}

.tag-star.warm .star-content {
    padding: 7px 11px;
    font-size: 12px;
}

.tag-star.cool .star-content {
    padding: 6px 10px;
    font-size: 11px;
}

/* 优雅的加载更多按钮 */
.load-more-section {
    display: flex;
    justify-content: center;
    margin-top: 8px;
}

.load-more-elegant {
    display: flex;
    align-items: center;
    gap: 6px;
    padding: 8px 16px;
    background: rgba(0, 200, 81, 0.1);
    color: #00c851;
    border: 1px solid rgba(0, 200, 81, 0.2);
    border-radius: 12px;
    cursor: pointer;
    font-size: 12px;
    font-weight: 500;
    transition: all 0.3s ease;
}

.load-more-elegant:hover:not(:disabled) {
    background: rgba(0, 200, 81, 0.15);
    border-color: rgba(0, 200, 81, 0.8);
}

.load-more-elegant:disabled {
    opacity: 0.6;
    cursor: not-allowed;
}

.elegant-loading {
    display: flex;
    align-items: center;
    gap: 6px;
}

.loading-pulse {
    width: 8px;
    height: 8px;
    background: #00c851;
    border-radius: 50%;
    animation: pulse 1.5s infinite;
}

@keyframes pulse {

    0%,
    100% {
        transform: scale(0.8);
        opacity: 0.5;
    }

    50% {
        transform: scale(1.2);
        opacity: 1;
    }
}

.load-more-text {
    display: flex;
    align-items: center;
    gap: 4px;
}

.more-icon {
    font-size: 14px;
    font-weight: 600;
}

/* 统计信息卡片 */
.stats-widget {
    margin-top: 16px;
}

.stats-content {
    display: flex;
    align-items: center;
    gap: 12px;
}

.stat-item {
    display: flex;
    align-items: center;
    gap: 6px;
    flex: 1;
}

.stat-icon {
    width: 24px;
    height: 24px;
    border-radius: 8px;
    display: flex;
    align-items: center;
    justify-content: center;
    color: white;
}

.articles-stat {
    background: linear-gradient(135deg, #00c851, #00a844);
}

.tags-stat {
    background: linear-gradient(135deg, #00ff88, #00e676);
}

.categories-stat {
    background: linear-gradient(135deg, #00e676, #00c851);
}

.stat-text {
    display: flex;
    flex-direction: column;
    gap: 2px;
}

.stat-number {
    font-size: 14px;
    font-weight: 700;
    color: #1f2937;
}

.stat-label {
    font-size: 10px;
    color: #6b7280;
    font-weight: 500;
}

.stat-divider {
    width: 1px;
    height: 20px;
    background: rgba(0, 0, 0, 0.1);
}

/* ========== 强制清除毛玻璃效果 ========== */
.main-content,
.main-content *,
.articles-section,
.articles-section *,
.articles-grid,
.articles-grid *,
.article-card-wrapper,
.article-card-wrapper * {
    backdrop-filter: none !important;
    -webkit-backdrop-filter: none !important;
}

/* ========== 响应式设计 ========== */
@media (max-width: 768px) {
    .main-content {
        padding: 76px 12px 24px;
    }

    .content-grid {
        grid-template-columns: 1fr;
    }

    .article-card {
        flex-direction: column;
        height: auto;
        min-height: 280px;
    }

    .article-cover {
        width: 100%;
        height: 180px;
    }

    .article-content {
        padding: 20px;
    }

    .title-with-category {
        flex-direction: column;
        gap: 8px;
        align-items: flex-start;
    }

    .article-category-tag {
        margin-top: 0;
        align-self: flex-start;
    }

    .search-modal-container {
        margin-top: 5vh;
    }

    .sidebar {
        order: -1;
    }
}

@media (max-width: 768px) {
    .navbar-container {
        padding: 12px 12px;
    }
}

@media (max-width: 480px) {
    .navbar {
        height: 56px;
    }

    .navbar-container {
        padding: 8px 12px;
    }
}

.logo-subtitle {
    display: none;
}

.search-button {
    width: 32px;
    height: 32px;
}

.section-title {
    font-size: 20px;
}

.article-title {
    font-size: 18px;
}

.nav-tab {
    padding: 6px 12px;
    font-size: 13px;
}
</style>